import React from 'react';
import { Box, HStack, VStack, Text, Circle, Flex } from '@chakra-ui/react';

interface StepNavigationProps {
  activeStep: number;
  setActiveStep: (step: number) => void;
  steps: string[];
}

export const StepNavigation: React.FC<StepNavigationProps> = ({
  activeStep,
  setActiveStep,
  steps,
}) => {
  return (
    <>
      {/* Desktop Version - Full text */}
      <Box display={{ base: 'none', lg: 'block' }} width="100%">
        <Flex justify="space-between" align="center" position="relative" px={4}>
          {/* Progress line */}
          <Box
            position="absolute"
            top="20px"
            left="0"
            right="0"
            height="2px"
            bg="gray.200"
            zIndex={0}
          >
            <Box
              height="100%"
              bg="blue.500"
              width={`${(activeStep / (steps.length - 1)) * 100}%`}
              transition="width 0.3s ease"
            />
          </Box>

          {/* Steps */}
          {steps.map((step, index) => {
            const isActive = activeStep === index;
            const isCompleted = activeStep > index;

            return (
              <VStack
                key={index}
                spacing={2}
                cursor="pointer"
                onClick={() => setActiveStep(index)}
                zIndex={1}
                flex="1"
                maxW="150px"
              >
                <Circle
                  size="40px"
                  bg={isActive ? 'blue.500' : isCompleted ? 'green.500' : 'white'}
                  border="2px solid"
                  borderColor={isActive ? 'blue.500' : isCompleted ? 'green.500' : 'gray.300'}
                  color={isActive || isCompleted ? 'white' : 'gray.500'}
                  fontWeight="bold"
                  fontSize="sm"
                  transition="all 0.2s"
                  _hover={{
                    transform: 'scale(1.1)',
                    borderColor: isActive ? 'blue.600' : isCompleted ? 'green.600' : 'gray.400',
                  }}
                >
                  {index + 1}
                </Circle>
                <Text
                  fontSize="xs"
                  fontWeight={isActive ? 'bold' : 'medium'}
                  color={isActive ? 'blue.600' : isCompleted ? 'green.600' : 'gray.600'}
                  textAlign="center"
                  lineHeight="1.2"
                  noOfLines={2}
                  transition="all 0.2s"
                >
                  {step}
                </Text>
              </VStack>
            );
          })}
        </Flex>
      </Box>

      {/* Tablet Version - Abbreviated text */}
      <Box display={{ base: 'none', md: 'block', lg: 'none' }} width="100%">
        <HStack spacing={1} justify="center" flexWrap="wrap" gap={2}>
          {steps.map((step, index) => {
            const isActive = activeStep === index;
            const isCompleted = activeStep > index;

            return (
              <HStack
                key={index}
                spacing={2}
                px={3}
                py={2}
                cursor="pointer"
                onClick={() => setActiveStep(index)}
                bg={isActive ? 'blue.500' : isCompleted ? 'green.50' : 'white'}
                color={isActive ? 'white' : isCompleted ? 'green.700' : 'gray.600'}
                borderRadius="md"
                border="1px solid"
                borderColor={isActive ? 'blue.500' : isCompleted ? 'green.200' : 'gray.200'}
                transition="all 0.2s"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'sm',
                }}
              >
                <Circle
                  size="24px"
                  bg={isActive ? 'blue.600' : isCompleted ? 'green.500' : 'gray.100'}
                  color={isActive || isCompleted ? 'white' : 'gray.600'}
                  fontSize="xs"
                  fontWeight="bold"
                >
                  {index + 1}
                </Circle>
                <Text fontSize="xs" fontWeight="medium" noOfLines={1}>
                  {step}
                </Text>
              </HStack>
            );
          })}
        </HStack>
      </Box>

      {/* Mobile Version - Compact circles only with active step name below */}
      <Box display={{ base: 'block', md: 'none' }} width="100%">
        <VStack spacing={3}>
          <HStack spacing={2} justify="center" flexWrap="wrap">
            {steps.map((step, index) => {
              const isActive = activeStep === index;
              const isCompleted = activeStep > index;

              return (
                <Circle
                  key={index}
                  size="36px"
                  bg={isActive ? 'blue.500' : isCompleted ? 'green.500' : 'white'}
                  border="2px solid"
                  borderColor={isActive ? 'blue.500' : isCompleted ? 'green.500' : 'gray.300'}
                  color={isActive || isCompleted ? 'white' : 'gray.500'}
                  fontWeight="bold"
                  fontSize="sm"
                  cursor="pointer"
                  onClick={() => setActiveStep(index)}
                  transition="all 0.2s"
                  _hover={{
                    transform: 'scale(1.1)',
                  }}
                >
                  {index + 1}
                </Circle>
              );
            })}
          </HStack>
          <Text
            fontSize="sm"
            fontWeight="bold"
            color="blue.600"
            textAlign="center"
          >
            {activeStep + 1}. {steps[activeStep]}
          </Text>
        </VStack>
      </Box>
    </>
  );
};

